import { Title } from '@sr-sdks/api-sdk-axios';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import { createClientsMap } from '../utils/helpers/clients';

/** Shown when a title has owners the resolve could not name. */
export const UNRESOLVED_CLIENT_NAME = 'Unknown client';

/** Mirrors `ClientSearchParamsDto`'s `@ArrayMaxSize(100)` on `clientIds`. */
export const MAX_CLIENT_IDS = 100;

/**
 * Splits ids into lookups the clients endpoint accepts, in order. Batched
 * rather than truncated: dropping the overflow loses names silently.
 */
export const batchClientIds = (clientIds: string[]) =>
  Array.from(
    { length: Math.ceil(clientIds.length / MAX_CLIENT_IDS) },
    (_, index) =>
      clientIds.slice(index * MAX_CLIENT_IDS, (index + 1) * MAX_CLIENT_IDS),
  );

/** Fixed, not `clientIds.length`: a length of 0 reaches `@Min(1)` as a 400. */
const CLIENT_PAGE_SIZE = MAX_CLIENT_IDS;

/**
 * Resolves client ids to names. Scoped to the ids asked for, not the workspace:
 * the workspace-wide endpoint caps at 100 and silently loses the rest (APP-2388).
 */
export const useClientNamesByIds = (
  clientIds?: (string | undefined)[],
  options: { enabled?: boolean; workspaceId?: string } = {},
) => {
  const { enabled = true, workspaceId } = options;

  /** Sorted so the cache key is stable across Mongo's ordering. */
  const idBatches = useMemo(() => {
    const unique = [...new Set((clientIds ?? []).filter(Boolean) as string[])];

    return batchClientIds(unique.sort());
  }, [clientIds]);

  const isQueryEnabled = enabled && Boolean(workspaceId);

  const clientsQueries = useQueries({
    queries: idBatches.map((batch) => ({
      ...queryOptionsGetClientsPaginated({
        clientIds: batch,
        pageSize: CLIENT_PAGE_SIZE,
        workspaceId,
      }),
      enabled: isQueryEnabled,
    })),
  });

  /**
   * Holds data we can name from. Deliberately not `isSuccess`: a failed refetch
   * keeps the data cached while flipping status to error, and the app polls on
   * an interval, so that would blank names whenever a poll missed.
   */
  const hasUsableData = (query: (typeof clientsQueries)[number]) =>
    Boolean(query.data) && !query.isPlaceholderData;

  /** Every batch freshly in: the only state where an absent id means *unowned*. */
  const isFullySettled =
    clientsQueries.length > 0 &&
    clientsQueries.every(
      (query) => query.isSuccess && !query.isPlaceholderData,
    );

  const usableCount = clientsQueries.filter(hasUsableData).length;

  /** Names degrade per batch, unlike ownership. */
  const items = useMemo(
    () =>
      clientsQueries
        .filter(hasUsableData)
        .flatMap((query) => query.data?.items ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usableCount, clientsQueries.map((query) => query.dataUpdatedAt).join(',')],
  );

  const clientNameById = useMemo(() => createClientsMap(items), [items]);

  const getClientNames = (lookupIds?: string[]) =>
    (lookupIds ?? [])
      .map((id) => clientNameById.get(id)?.clientName)
      .filter((name): name is string => Boolean(name));

  const isLoading = clientsQueries.some(
    (query) => query.isLoading || query.isPlaceholderData,
  );

  /**
   * Per id, not per call: a failed batch must not relabel names another batch
   * resolved, nor an id whose own batch is still in flight — with more than one
   * batch, `isError` can already be true while others are loading.
   */
  const getClientNamesOrFallback = (lookupIds?: string[]) => {
    const names = getClientNames(lookupIds);

    if (names.length || !lookupIds?.length || isLoading) return names;

    return clientsQueries.some((query) => query.isError)
      ? [UNRESOLVED_CLIENT_NAME]
      : names;
  };

  /** `undefined` is "not known yet", which callers must not read as "unowned". */
  const resolvedClientIds = useMemo(
    // Memoised: callers use this as an effect dependency.
    () => (isFullySettled ? new Set(clientNameById.keys()) : undefined),
    [isFullySettled, clientNameById],
  );

  return {
    clientNameById,
    clientsQueries,
    getClientNames,
    getClientNamesOrFallback,
    /** Any batch failing means the names on screen are incomplete. */
    isError: clientsQueries.some((query) => query.isError),
    /** True while names cannot be trusted — includes the placeholder window. */
    isLoading,
    resolvedClientIds,
  };
};

/**
 * `useClientNamesByIds` scoped to a page of titles: flattens their
 * `ownerClientIds` into the id list it takes. Shared by the Titles list and
 * the Home "Titles" snapshot so a later change to how ownership resolves
 * lands once, not per call site.
 */
export const useOwnerClientNames = (
  titles: Title[],
  options: { enabled?: boolean; workspaceId?: string } = {},
) => {
  const ownerClientIds = useMemo(
    () => titles.flatMap((title) => title.ownerClientIds ?? []),
    [titles],
  );

  return useClientNamesByIds(ownerClientIds, options);
};
